/*
 * The star rating is shown without a review count on purpose.
 *
 * It used to read "rating based on 34 local reviews". That 34 was hardcoded
 * in the markup - it was never read from anywhere and had no relationship to
 * the real Google review total, so it could only drift further out of date.
 * An unverifiable figure presented as a fact is worse than no figure.
 *
 * A live count fetched from Google would be fine. The full implementation
 * still exists in GoogleReviews.js (currently unrendered, and pointed at a
 * decommissioned proxy - see CLAUDE.md section 7). If that proxy is ever
 * replaced, take the count from there rather than typing a number in here.
 */

import { Fragment, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Container } from "../styles/Layout";
import { Star } from "@material-ui/icons";
import { size } from "../styles/Breakpoints";
import { brandLettering, brandBlue } from "./BrandName";
import useNearViewport from "../../hooks/useNearViewport";

const Section = styled.section`
	background: url("${(props) => props.background}");
`;

/*
 * Fixed behind the page and always at least the size of the viewport, so the
 * banner shows video wherever it is on screen. It used to be anchored at 35%
 * (25% on mobile) of the viewport height at its natural size, which left the
 * bottom of the screen uncovered - invisible while the banner sat at the top
 * of the page, but a white band under the text once it moved further down.
 */
const Video = styled.video`
	position: fixed;
	z-index: -1;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	width: auto;
	height: auto;
	min-width: 100%;
	min-height: 100%;
	filter: brightness(0.5);
`;

const InfoWrap = styled.div`
	position: relative;
	padding: 3rem 0;
	z-index: 0;
`;

const Paragraph = styled.p`
	color: white;
	width: 70%;
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
	@media screen and (max-width: ${size.sm}px) {
		width: 100%;
	}
`;

const Title = styled.h1`
	color: white;
	margin-top: 0;
	text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
`;

/* Optional second line of the h1, set smaller so the headline stays short. */
const Tagline = styled.span`
	display: block;
	margin-top: 0.4rem;
	font-size: 0.6em;
	color: ${(props) => props.theme.lightblue};
`;

/*
 * Optional row of links under the paragraphs, lettered like PEOPLE in the
 * headline: same font, colour and size (2rem is the h1's 36px on this site).
 * On mobile the row stacks and drops the separators, which would otherwise
 * dangle at the end of a wrapped line.
 */
const LinkRow = styled.nav`
	${brandLettering}
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.25rem 0.5em;
	margin-top: 1.75rem;
	font-size: 2rem;
	line-height: 1.2;
	color: ${brandBlue};
	text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
	& a {
		color: inherit;
		transition: color 0.2s;
	}
	& a:hover,
	& a:focus-visible {
		color: white;
	}
	@media screen and (max-width: ${size.sm}px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const Separator = styled.span`
	@media screen and (max-width: ${size.sm}px) {
		display: none;
	}
`;

const Reviews = styled.div`
	color: white;
	display: flex;
	align-items: center;
	font-weight: 500;
	@media screen and (max-width: ${size.sm}px) {
		font-size: 0.75rem;
	}
`;

const StarWrap = styled.span`
	margin: 0 0.5rem;
	color: ${(props) => props.theme.star};
	padding-top: 0.15rem;
	& svg {
		font-size: 1.3rem;
		padding: 0.1rem;
	}
	@media screen and (max-width: ${size.sm}px) {
		& svg {
			font-size: 0.8rem;
			padding: 0;
		}
	}
`;

/*
 * `subtitle` is a string, or an array of strings for several paragraphs.
 * `links` is an optional array of { label, to } for in-app routes.
 */
export default function HeroBanner({ title, tagline, subtitle, links, video, background, showRating }) {
	const paragraphs = Array.isArray(subtitle) ? subtitle : [subtitle];

	/*
	 * The background video is several MB, and on the home page the banner sits
	 * below the slider. It used to autoplay - so download - on page load for
	 * every visitor. Now it has preload="none" and only starts once the banner
	 * is near the viewport; until then (and for anyone who never scrolls that
	 * far) only the poster image loads. It never starts for visitors who ask
	 * for reduced data (Save-Data) or reduced motion; they keep the poster.
	 */
	const sectionRef = useRef(null);
	const videoRef = useRef(null);
	const nearViewport = useNearViewport(sectionRef, "200px");

	useEffect(() => {
		const el = videoRef.current;
		if (!video || !nearViewport || !el) return;
		const saveData = navigator.connection && navigator.connection.saveData;
		const reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (saveData || reducedMotion) return;
		const playing = el.play();
		// Autoplay can still be refused (e.g. low-power mode); the poster stays.
		if (playing && playing.catch) playing.catch(() => {});
	}, [video, nearViewport]);

	return (
		<Section ref={sectionRef} background={video ? "none" : background}>
			<Container>
				<InfoWrap>
					<Title>
						{title}
						{tagline && <Tagline>{tagline}</Tagline>}
					</Title>
					{paragraphs.map((text, i) => (
						<Paragraph key={i}>{text}</Paragraph>
					))}
					{links && links.length > 0 && (
						<LinkRow aria-label="Our services">
							{links.map((link, i) => (
								<Fragment key={link.to}>
									{i > 0 && <Separator aria-hidden="true">·</Separator>}
									<Link to={link.to}>{link.label}</Link>
								</Fragment>
							))}
						</LinkRow>
					)}
					{showRating && (
						<Reviews>
							Excellent
							<StarWrap>
								<Star />
								<Star />
								<Star />
								<Star />
								<Star />
							</StarWrap>
							rating
						</Reviews>
					)}
				</InfoWrap>
			</Container>
			{video && (
				<Video ref={videoRef} poster={background} preload="none" muted loop playsInline>
					{/* playsInline: iOS Safari won't play a background video inline without it. */}
					<source src={video} type="video/mp4" />
				</Video>
			)}
		</Section>
	);
}
